import {
    Table,
    Input,
    Button,
    Space,
    Tooltip,
    Popconfirm,
    Tag,
    Select,
    Drawer,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    LockOutlined,
} from "@ant-design/icons";
import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    RequestGetAllCriteria,
    RequestDeleteCriterion,
    RequestCreateCriterion,
    RequestUpdateCriterion,
} from "../../Actions/critere.action";
import CriterionFormModal from "./CriterionFormModal";
import TemplateCritere from "./TemplateCritere";
import {useCanEditCriteria} from "../../utils/roles";
import { fetchCommercialUsage } from "../../Actions/commercialControl.action";
import {
    canCreateCustomCriteria,
    canEditCriteriaPrompts,
    canGenerateCriteriaPrompts,
    canMutateCustomCriterion,
    getCustomCriteriaSnapshot,
    isCustomCriterion,
} from "../../utils/commercialCapabilities";
import {useTranslation} from "react-i18next";
import {resolveCriterionText} from "../../utils/criterionTranslations";
import {
    getCriterionReviewProfiles,
    isSystemCriterionForReviewProfiles,
} from "../../utils/reviewProfiles";
import "./criteres.css";

const CriterionTablePage = () => {
    const {t, i18n} = useTranslation("criteriaView");
    const language = i18n.resolvedLanguage || i18n.language;
    const dispatch = useDispatch();
    const {criteria = [], loading} = useSelector((state) => state.criteria);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const canEditCriteria = useCanEditCriteria();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCriterion, setEditingCriterion] = useState(null);
    const [search, setSearch] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [drawerOpened, openDrawer] = useState(false);

    useEffect(() => {
        dispatch(RequestGetAllCriteria());
    }, [dispatch]);

    useEffect(() => {
        if (commercialControl.usageSnapshot || commercialControl.isLoadingUsage) return;
        dispatch(fetchCommercialUsage()).catch(() => {});
    }, [commercialControl.isLoadingUsage, commercialControl.usageSnapshot, dispatch]);

    const capabilities = commercialControl.capabilities || commercialControl.usageSnapshot?.capabilities || {};
    const rawCustomCriteria = getCustomCriteriaSnapshot(commercialControl);
    const localCustomCriteriaUsed = criteria.filter(isCustomCriterion).length;
    const customCriteria = (() => {
        if (rawCustomCriteria?.unlimited === true) return rawCustomCriteria;
        const snapshotUsed = Number(rawCustomCriteria?.used);
        const used = Math.max(
            Number.isFinite(snapshotUsed) ? snapshotUsed : 0,
            localCustomCriteriaUsed,
        );
        const limit = Number(rawCustomCriteria?.limit ?? capabilities?.custom_criteria_limit_total);
        if (!Number.isFinite(limit)) {
            return rawCustomCriteria;
        }
        const adjustments = Number(rawCustomCriteria?.adjustments);
        const effectiveLimit = limit + (Number.isFinite(adjustments) ? adjustments : 0);
        const remaining = Math.max(0, effectiveLimit - used);
        return {
            ...rawCustomCriteria,
            used,
            limit,
            remaining,
            can_create: rawCustomCriteria?.can_create === false ? false : remaining > 0,
        };
    })();
    const plan = commercialControl.plan || commercialControl.usageSnapshot?.plan || {};
    const canCreateCriterion = canCreateCustomCriteria(capabilities, customCriteria);
    const promptEditingAllowed = canEditCriteriaPrompts(capabilities);
    const promptGenerationAllowed = canGenerateCriteriaPrompts(capabilities);
    const createLimitTooltip = customCriteria?.limit
        ? t("commercial.customCriteriaLimit", {
            used: customCriteria.used ?? 0,
            limit: customCriteria.limit,
        })
        : t("commercial.customCriteriaUnavailable");
    const renderReviewProfileTags = (criterion) => {
        if (!isSystemCriterionForReviewProfiles(criterion)) {
            return <span className="criteria-table__muted">{t("reviewProfiles.notApplicable")}</span>;
        }

        const profiles = getCriterionReviewProfiles(criterion);
        if (!profiles.length) {
            return <Tag className="criteria-review-profile-tag">{t("reviewProfiles.allSystem")}</Tag>;
        }

        const labels = profiles.map((profile) => t(`reviewProfiles.options.${profile}`));
        if (labels.length > 1) {
            return (
                <Tooltip title={labels.join(" · ")}>
                    <span className="criteria-review-profile-summary">
                        {labels.length} {t("table.reviewProfiles").toLowerCase()}
                    </span>
                </Tooltip>
            );
        }

        return (
            <Tag className="criteria-review-profile-tag">
                {labels[0]}
            </Tag>
        );
    };

    const clientOptions = [
        ...new Set(criteria.map(c => c.client?.name || "VerdictIA"))
    ].map(name => ({label: name, value: name}));

    const creatorOptions = [
        ...new Set(criteria.map(c => `${c.creator?.name || ""} ${c.creator?.surname || ""}`.trim()))
    ].filter(Boolean).map(name => ({label: name, value: name}));

    const filteredData = criteria.filter((criterion) => {
        const searchValue = search.toLowerCase();
        const creatorName = `${criterion.creator?.name || ""} ${criterion.creator?.surname || ""}`.trim();
        const text = resolveCriterionText(criterion, language);

        const matchSearch =
            text.title?.toLowerCase().includes(searchValue) ||
            criterion.section?.toLowerCase().includes(searchValue) ||
            text.description?.toLowerCase().includes(searchValue);

        const matchClient =
            !selectedClient ||
            (criterion.client?.name || "VerdictIA") === selectedClient;

        const matchCreator =
            !selectedCreator ||
            creatorName === selectedCreator;

        const matchStatus =
            selectedStatus === null ||
            selectedStatus === undefined ||
            (selectedStatus === "active" && criterion.is_active) ||
            (selectedStatus === "inactive" && !criterion.is_active);

        return matchSearch && matchClient && matchCreator && matchStatus;
    });

    const openCreateModal = () => {
        if (!canCreateCriterion) return;
        setEditingCriterion(null);
        setIsModalVisible(true);
    };

    const openEditModal = (criterion) => {
        setEditingCriterion(criterion);
        setIsModalVisible(true);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setEditingCriterion(null);
    };

    const handleSubmitCriterion = async (formValues) => {
        let result;
        if (editingCriterion) {
            result = await dispatch(RequestUpdateCriterion(editingCriterion.id, formValues));
        } else {
            result = await dispatch(RequestCreateCriterion(formValues));
        }
        if (result?.success) {
            dispatch(fetchCommercialUsage()).catch(() => {});
            closeModal();
        }
    };

    const canManageCriterion = (record) => {
        const permissionRecord = {
            ...record,
            client_id: record.client_id ?? record.client?.id,
            user_id: record.user_id ?? record.created_by ?? record.creator?.id,
        };
        return canEditCriteria(permissionRecord);
    };

    const canMutateCriterion = (record) =>
        canManageCriterion(record) &&
        canMutateCustomCriterion(record, capabilities, customCriteria, plan);

    const showActionsColumn = filteredData.some(canMutateCriterion);

    const columns = [
        {
            title: t("table.section"),
            dataIndex: "section",
            key: "section",
            width: 170,
            filters: [...new Set(criteria.map(c => c.section))].filter(Boolean).map(section => ({
                text: section,
                value: section
            })),
            onFilter: (value, record) => record.section === value,
            render: (section) => <span className="criteria-table__section">{section || "-"}</span>,
        },
        {
            title: t("table.title"),
            dataIndex: "title",
            key: "title",
            width: 300,
            render: (_, record) => (
                <span className="criteria-table__title">
                    {resolveCriterionText(record, language).title || "-"}
                </span>
            ),
        },
        {
            title: t("table.description"),
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            width: 320,
            render: (_, record) => {
                const text = resolveCriterionText(record, language).description;
                return (
                <Tooltip title={text}>
                    <span className="criteria-table__muted">{text || "-"}</span>
                </Tooltip>
                );
            },
        },
        {
            title: t("table.prompt"),
            dataIndex: "prompt",
            key: "prompt",
            render: (text) => (
                <Tooltip title={text}>
                    <span className="criteria-table__muted">{text?.length > 48 ? text.slice(0, 48) + "..." : text || "-"}</span>
                </Tooltip>
            ),
            ellipsis: true,
            width: 280,
        },
        {
            title: t("table.creator"),
            dataIndex: "creator",
            key: "creator",
            width: 150,
            render: (creator) => (
                <span>{creator?.name || "-"} {creator?.surname || ""}</span>
            ),
        },
        {
            title: t("table.client"),
            dataIndex: "client",
            key: "client",
            width: 150,
            render: (client) => <span>{client?.name || "VerdictIA"}</span>,
        },
        {
            title: t("table.reviewProfiles"),
            dataIndex: "review_profiles",
            key: "review_profiles",
            width: 150,
            render: (_, record) => renderReviewProfileTags(record),
        },
        {
            title: t("table.active"),
            dataIndex: "is_active",
            key: "is_active",
            width: 110,
            render: (active) => (
                <Tag className={active ? "criteria-status criteria-status--active" : "criteria-status criteria-status--inactive"}>
                    {active ? t("status.active") : t("status.inactive")}
                </Tag>
            ),
        },
        {
            title: t("table.actions"),
            key: "actions",
            width: 110,
            fixed: "right",
            render: (_, record) => {
                const hasPermission = canMutateCriterion(record);

                return (
                    <Space size={4}>
                        {hasPermission && (
                            <Tooltip title={t("actions.edit")}>
                                <Button
                                    type="text"
                                    className="criteria-action-btn"
                                    icon={<EditOutlined/>}
                                    onClick={() => openEditModal(record)}
                                />
                            </Tooltip>
                        )}
                        {hasPermission && (
                            <Popconfirm
                                title={t("actions.confirmDelete")}
                                onConfirm={async () => {
                                    const result = await dispatch(RequestDeleteCriterion(record.id));
                                    if (result?.success) {
                                        dispatch(fetchCommercialUsage()).catch(() => {});
                                    }
                                }}
                                okText={t("actions.confirm")}
                                cancelText={t("actions.cancel")}
                            >
                                <Tooltip title={t("actions.delete")}>
                                    <Button
                                        type="text"
                                        className="criteria-action-btn criteria-action-btn--danger"
                                        icon={<DeleteOutlined/>}
                                        danger
                                    />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Space>
                );
            }
        },
    ].filter((column) => column.key !== "actions" || showActionsColumn);

    const tableScrollX = showActionsColumn ? 1630 : 1520;

    return (
        <div className="criteria-page">
            <div className="criteria-page__header">
                <div>
                    <p className="criteria-page__subtitle criteria-page__subtitle--compact">
                        {filteredData.length} {t("labels.displayed")}
                    </p>
                </div>
                <div className="criteria-page__actions">
                    <Button className="criteria-page__secondary-btn" icon={<PlusOutlined/>} onClick={() => openDrawer(true)}>
                        {t("actions.manageTemplates")}
                    </Button>
                    <Tooltip title={canCreateCriterion ? "" : createLimitTooltip}>
                        <span>
                            <Button
                                type="primary"
                                className="criteria-page__primary-btn verdictia-primary-btn"
                                icon={canCreateCriterion ? <PlusOutlined/> : <LockOutlined/>}
                                onClick={openCreateModal}
                                disabled={!canCreateCriterion}
                            >
                                {t("actions.add")}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            <div className="criteria-page__filters">
                <div className="criteria-page__filter criteria-page__filter--search">
                    <Input
                        placeholder={t("search.placeholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        prefix={<SearchOutlined/>}
                        allowClear
                    />
                </div>
                <div className="criteria-page__filter">
                    <Select
                        allowClear
                        style={{width: "100%"}}
                        placeholder={t("search.filterClient")}
                        value={selectedClient}
                        onChange={(value) => setSelectedClient(value)}
                        options={clientOptions}
                    />
                </div>
                <div className="criteria-page__filter">
                    <Select
                        allowClear
                        style={{width: "100%"}}
                        placeholder={t("search.filterCreator")}
                        value={selectedCreator}
                        onChange={(value) => setSelectedCreator(value)}
                        options={creatorOptions}
                    />
                </div>
                <div className="criteria-page__filter">
                    <Select
                        allowClear
                        style={{width: "100%"}}
                        placeholder={t("search.filterStatus")}
                        value={selectedStatus}
                        onChange={(value) => setSelectedStatus(value)}
                        options={[
                            {label: t("filters.all"), value: null},
                            {label: t("filters.active"), value: "active"},
                            {label: t("filters.inactive"), value: "inactive"},
                        ]}
                    />
                </div>
            </div>

            <div className="criteria-page__table-card">
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    size="middle"
                    pagination={{pageSize: 8, showSizeChanger: false}}
                    scroll={{x: tableScrollX, y: "calc(100vh - 410px)"}}
                    tableLayout="fixed"
                    rowKey="id"
                    rowClassName={(record) => (!record.is_active ? "inactive-row" : "")}
                />
            </div>

            <CriterionFormModal
                open={isModalVisible}
                onClose={closeModal}
                onSubmit={handleSubmitCriterion}
                initialValues={editingCriterion}
                existingSections={criteria.map(c => c.section)}
                canEditPrompt={promptEditingAllowed}
                canGeneratePrompt={promptGenerationAllowed}
            />

            <Drawer
                title={t("drawer.title")}
                width={1300}
                onClose={() => openDrawer(false)}
                open={drawerOpened}
                destroyOnClose
                maskClosable
                className="criteria-template-drawer"
            >
                <TemplateCritere criteria={filteredData} clients={[]} users={[]}/>
            </Drawer>
        </div>
    );
};

export default CriterionTablePage;
